/** @jsx jsx */
import { ReactNode } from 'react';

import { jsx } from '@emotion/core';
import {
  Alert,
  AlertDescription,
  LegacyButton,
  Margin,
  Text,
} from '@zapier/design-system';

import Link from 'app/common/components/Link';
import EditorLayout from 'app/developer-v3/layouts/EditorLayout';
import { AppSummary } from 'app/developer-v3/types';
import { PARTNER_EMAIL_ADDRESS } from 'app/developer-v3/constants';
import { getAppUrl } from 'app/developer-v3/utils';
import { useCurrentVersionReadOnlyReason } from 'app/developer-v3/hooks';

type Props = {
  appSummary: AppSummary;
  children: ReactNode;
  params: { appId: string };
  router: { current: { url: string } };
};

function EmbedSettings(props: Props) {
  const { appSummary, children, router } = props;
  const { slug, appId } = appSummary;

  const isReadOnly = !!useCurrentVersionReadOnlyReason();
  const appUrl = getAppUrl(appId);

  if (!slug) {
    return (
      <Margin margin="25px 30px">
        <Text margin="0 0 25px" tag="h1" type="ParagraphHeader3">
          Settings
        </Text>
        <Alert
          status="error"
          title="You need Workflow API access to view or modify the embed settings"
        >
          <AlertDescription>
            Request access to our Workflow API to see your Client ID and other
            settings or contact{' '}
            <Link href={`mailto:${PARTNER_EMAIL_ADDRESS}`}>
              {PARTNER_EMAIL_ADDRESS}
            </Link>{' '}
            if you think you should already have it.
          </AlertDescription>
          <LegacyButton
            color="danger"
            hasShadow={true}
            href="https://zapier.typeform.com/to/atnWuF"
            target="_blank"
          >
            Request access
          </LegacyButton>
        </Alert>
      </Margin>
    );
  }

  return (
    <EditorLayout
      currentUrl={router.current.url}
      isDisabled={isReadOnly}
      navConfig={[
        {
          text: 'Credentials',
          href: `${appUrl}/embed-settings/credentials`,
        },
        {
          text: 'Embedding Domains',
          href: `${appUrl}/embed-settings/domains`,
        },
        {
          text: 'Redirect URIs',
          href: `${appUrl}/embed-settings/redirects`,
        },
      ]}
      title="Settings"
    >
      {children}
    </EditorLayout>
  );
}

export default EmbedSettings;
